import '../../utils/polyfills'

import keyboardFocus from 'keyboard-focus'
import PropTypes from 'prop-types'
import React from 'react'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'

import { BodyScrollBlocker, BodyScrollToTop, DefaultHead, Footer, Header, Layout, Sidebar } from '../../components'
import { actions as AppActions, selectors as AppSelectors } from '../../redux/app/redux'
import router from '../../router'
import { fonts, reset, global } from '../../theme'
import { localize } from '../../utils/TranslationUtils'
import { getCanonicalURL } from '../../utils/URLUtils'
import { routesInfos } from '../../root/App/constants'
import { transformDomainRoutes } from '../../helpers/RouteHelpers'
import { ParallaxProvider } from 'react-scroll-parallax'



const mapSelectors = state => ({
  statusCode: 200,
  isNavOpen: AppSelectors.isNavOpen(state),
})

const mapActions = dispatch => ({
  initApp: () => dispatch(AppActions.init()),
  setNavOpen: isNavOpen => dispatch(AppActions.setNavOpen(isNavOpen)),
})

@localize
@connect(mapSelectors, mapActions)
class App extends React.PureComponent {

  static propTypes = {
    initApp: PropTypes.func.isRequired,
    statusCode: PropTypes.number.isRequired,
    activeRoute: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    isNavOpen: PropTypes.bool.isRequired,
    setNavOpen: PropTypes.func.isRequired,
    domains: PropTypes.array,
    t: PropTypes.func.isRequired,
    p: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
  }

  componentWillMount = () => {
    const { initApp } = this.props

    initApp()
  }

  componentDidMount() {
    keyboardFocus(document)
  }

  handleClickHeaderButton = () => {
    this.props.setNavOpen(true)
  }

  handleClickSidebarClose = () => {
    this.props.setNavOpen(false)
  }

  renderScrollBlocker() {
    const { isNavOpen } = this.props

    if (isNavOpen) {
      return <BodyScrollBlocker />
    }
  }

  renderErrorPage() {
    return null
  }

  renderPage() {
    // Keep following unused variables, they are for removing unwanted props given to children
    // eslint-disable-next-line
    const { initApp, t, children, ...otherProps } = this.props

    return React.cloneElement(children, otherProps)
  }

  renderFooter() {
    // eslint-disable-next-line
    const { t, locale } = this.props

    return (
      <Footer
        niceCity={t('bbsNiceCity')}
        nicePhone={t('bbsNicePhone')}
        niceEmail={t('bbsNiceEmail')}
        niceAddress={t('bbsNiceAddress')}
        parisCity={t('bbsParisCity')}
        parisPhone={t('bbsParisPhone')}
        parisEmail={t('bbsParisEmail')}
        parisAddress={t('bbsParisAddress')}
        copyright={`${t('copyright')} ${new Date().getFullYear()}`}
        certificate={t('bbsCertificate')}
        legalNotice={t('bbsLegalNotice')}
        cookies={t('bbsCookies')}
        locale={locale}
      />
    )
  }

  render() {
    const { isNavOpen, locale, activeRoute, t, p, domains } = this.props

    const routesInfosEnhanced = transformDomainRoutes(domains, locale, activeRoute)

    const otherRoutes = Object.values(routesInfos).map(({ label, link, route }) => {
      const name = t(label)
      const routeParams = { locale }
      const isActiveRoute = route ? router(route, routeParams) === activeRoute : false

      return ({
        route,
        routeParams,
        link,
        name,
        active: isActiveRoute
      })
    })

    const project = p(activeRoute)
    const activeRouteName = routesInfosEnhanced
      .find(route => route.active)?.name || project?.title || 'Route'

    const url = `${process.env.APP_URL}${getCanonicalURL(activeRoute)}`

    return (
      <>
        <DefaultHead />
        <Helmet>
          <meta
            property="og:type"
            content="website"
          />
          <meta
            property="og:url"
            content={url}
          />
          <link
            rel="canonical"
            href={url}
          />
          <script src="https://cdnjs.cloudflare.com/ajax/libs/tarteaucitronjs/1.19.0/tarteaucitron.js"></script>

          <script type="text/javascript">
            {`tarteaucitron.init({
            "privacyUrl": "", /* Privacy policy url */
            "bodyPosition": "bottom", /* or top to bring it as first element for accessibility */

            "hashtag": "#tarteaucitron", /* Open the panel with this hashtag */
            "cookieName": "tarteaucitron", /* Cookie name */

            "orientation": "bottom", /* Banner position (top - bottom) */

            "groupServices": false, /* Group services by category */
            "showDetailsOnClick": false, /* Click to expand the description */
            "serviceDefaultState": "wait", /* Default state (true - wait - false) */

            "showAlertSmall": false, /* Show the small banner on bottom right */
            "cookieslist": false, /* Show the cookie list */

            "closePopup": false, /* Show a close X on the banner */

            "showIcon": true, /* Show cookie icon to manage cookies */
            //"iconSrc": "", /* Optionnal: URL or base64 encoded image */
            "iconPosition": "BottomRight", /* BottomRight, BottomLeft, TopRight and TopLeft */

            "adblocker": false, /* Show a Warning if an adblocker is detected */

            "DenyAllCta" : true, /* Show the deny all button */
            "AcceptAllCta" : true, /* Show the accept all button when highPrivacy on */
            "highPrivacy": true, /* HIGHLY RECOMMANDED Disable auto consent */
            "alwaysNeedConsent": false, /* Ask the consent for "Privacy by design" services */

            "handleBrowserDNTRequest": false, /* If Do Not Track == 1, disallow all */

            "removeCredit": false, /* Remove credit link */
            "moreInfoLink": true, /* Show more info link */

            "useExternalCss": false, /* If false, the tarteaucitron.css file will be loaded */
            "useExternalJs": false, /* If false, the tarteaucitron.js file will be loaded */

            //"cookieDomain": ".my-multisite-domaine.fr", /* Shared cookie for multisite */

            "readmoreLink": "", /* Change the default readmore link */

            "mandatory": true, /* Show a message about mandatory cookies */
            "mandatoryCta": true, /* Show the disabled accept button when mandatory on */

            //"customCloserId": "", /* Optional a11y: Custom element ID used to open the panel */

            "googleConsentMode": true, /* Enable Google Consent Mode v2 for Google ads and GA4 */

            "partnersList": false /* Show the number of partners on the popup/middle banner */
          });`}
          </script>

        </Helmet>
        <Header
          buttonLabel={activeRouteName}
          onClickButton={this.handleClickHeaderButton}
          locale={locale}
          t={t}
        />
        <Sidebar
          isOpen={isNavOpen}
          onPressClose={this.handleClickSidebarClose}
          activeRouteName={activeRouteName}
          routes={routesInfosEnhanced}
          otherRoutes={otherRoutes}
        />
        <BodyScrollToTop />
        <ParallaxProvider>
          <Layout>
            {this.renderScrollBlocker()}
            {this.renderErrorPage()}
            {this.renderPage()}
            {this.renderFooter()}
          </Layout>
        </ParallaxProvider>
        <style jsx>{reset}</style>
        <style jsx>{fonts}</style>
        <style jsx>{global}</style>
      </>
    )
  }

}

export default App
